import { Component, Inject, OnInit, ViewChild, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgForm } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { environment } from 'projects/gtw-elements/src/environments/environment';
import Highcharts from 'projects/gtw-elements/src/app/core/highcharts-init';
import { StrategyService } from '../../services/strategy.service';
import { ComponentService } from '../../services/component.service';
import { DataFormatterService } from '../../services/data-formatter.service';
import { ExDashboardEventService } from '../../services/event.service';
import { GlobalService } from '../../../../global/services/global.service';
import { DashboardComponent } from '../../models/DashboardComponent';
import { DataSheet } from '../../models/DataSheet';
import { StrategyFunctionDialogComponent } from '../strategy-function-dialog/strategy-function-dialog.component';
import * as _ from 'lodash-es';

import { safeJsonInput } from '../../../../shared/_utils/safe-json-input';
export interface EditComponentDialogData {
  editObj: DashboardComponent;
  sheetsObj: DataSheet[];
  fullData: boolean;
  dashboard_id: string;
  rowIndex: string;
  columns: string;
}

/** Default componentSettings tree for `chart` / `combinationChart` types.
 *  Mirrors what the legacy `chartSettingsCtrl` expected when binding to
 *  `componentSettings.chart.*`, `componentSettings.plotOptions.series.*`, etc.
 */
const CHART_SETTINGS_DEFAULTS = {
  chart: {
    type: 'column',
    options3d: {
      enabled: false,
      alpha: 0,
      beta: 0,
      depth: 0,
      viewDistance: 0,
    },
  },
  plotOptions: {
    series: {
      stacking: '',
      groupPadding: 0.2,
      dataLabels: {
        enabled: false,
        seriesAsLabel: false,
        inside: false,
        formatter: 'None',
        format: '{point.y}',
      },
    },
  },
  legend: { enabled: true },
  tooltip: {
    enabled: true,
    shared: false,
    formatter: 'None',
    pointFormat: '<span style="color:{point.color}">●</span> {series.name}: <b>{point.y}</b><br/>',
  },
  yAxis: {
    stackLabels: {
      enabled: false,
      formatter: 'None',
      format: '{total}',
    },
  },
  xAxis: {
    scrollbar: { enabled: false },
    max: undefined as number | undefined,
  },
  customColors: { enabled: false },
  disableSmoothScale: false,
  eventMessage: '',
};

@Component({
    selector: 'gtw-edit-component-dialog',
    templateUrl: './edit-component-dialog.component.html',
    styleUrls: ['./edit-component-dialog.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EditComponentDialogComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);


  // Note: this is only present once the General Settings panel has rendered
  // (it lives behind `*ngIf="accordions.generalSettings"`), so we declare it
  // optional. The Save button reads `chartForm?.invalid` because the template
  // can refresh before `@ViewChild` has resolved.
  @ViewChild('chartForm') chartForm?: NgForm;

  component: any;
  sheetData: DataSheet[];
  fullData: boolean;
  chartView: boolean = false;
  actionLoading: boolean = false;
  actionData: string[] = [];
  actionDataSafeCopy: string[] = [];
  strategyInputs: any[] = [];
  sheetDataSafeCopy: any[] = [];
  minimise: boolean = false;
  jsonView: boolean = false;
  compJsonObj: string = '';
  defaultFilterValues: string[] = [];

  definedComponentTypes: string[] = [];
  strategies: any;
  formatters: any;
  kpiStrategies: any[];

  /** Per-key `format` snapshot mirroring legacy `safeFormatCopy`. */
  private safeFormatCopy: Record<string, string | undefined> = {};

  accordions = {
    generalSettings: true,
    strategySettings: false,
    compSpecificSettings: false,
    kpis: false
  };

  private emptyKpiObj: any;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: EditComponentDialogData,
    private dialogRef: MatDialogRef<EditComponentDialogComponent>,
    private strategyService: StrategyService,
    private componentService: ComponentService,
    private dataFormatterService: DataFormatterService,
    private eventService: ExDashboardEventService,
    private globalService: GlobalService,
    private http: HttpClient,
    private dialog: MatDialog
  ) {
    this.sheetData = data.sheetsObj;
    this.fullData = data.fullData;
    this.formatters = this.dataFormatterService.getAllFormatters();
    this.kpiStrategies = this.componentService.getComponentStrategies('kpi');
    this.definedComponentTypes = this.componentService.getDefinedComponentList();
    this.strategies = this.strategyService.getAllStrategies();

    this.component = {
      previewMode: true,
      previewSheetData: {} as DataSheet,
      extraOptions: {
        componentSettings: {},
        strategySettings: {}
      },
      filterable: true
    };

    this.component = _.merge(this.component, _.cloneDeep(data.editObj));
    this.component.columns = this.component.columns ? parseInt(this.component.columns) : this.component.columns;

    // Make sure componentSettings has the full shape the chart-settings template
    // binds to. Without these defaults `[(ngModel)]` paths like
    // `componentSettings.chart.options3d.enabled` would throw on a fresh component.
    this.ensureChartSettingsDefaults();

    // Diagnostic — surface exactly what the dialog received and what survived
    // the merge so we can compare against the legacy AngularJS controller for
    // any saved component.
    console.log('[EditComponent] editObj received:', _.cloneDeep(data.editObj));
    console.log('[EditComponent] component after merge:', {
      title: this.component.title,
      component_id: this.component.component_id,
      component_type: this.component.component_type,
      strategy: this.component.strategy,
      srcSheet: this.component.srcSheet,
      strategySettings: _.cloneDeep(this.component.extraOptions?.strategySettings),
      kpis: _.cloneDeep(this.component.extraOptions?.kpis),
      componentSettings: _.cloneDeep(this.component.extraOptions?.componentSettings),
    });

    this.emptyKpiObj = {
      title: '',
      dashboard_id: this.component.dashboard_id || '',
      component_type: 'kpi',
      component_id: this.component.component_id,
      strategy: null,
      filterable: true,
      srcSheet: this.component.srcSheet || '',
      extraOptions: {
        label: null,
        strategySettings: { aggregateBy: null },
        componentSettings: {},
      },
    };

    if (this.component.component_type) {
      this.component.supportedStrategies = this.getSupportedStrategies();
    }

    if (this.component.component_id) {
      const removeFields = _.omit(this.component, ['title', 'dashboard_id', 'component_id', 'rowIndex']);
      this.compJsonObj = JSON.stringify(removeFields, null, 2);
    }
  }

  ngOnInit(): void {
    if (this.component.srcSheet) {
      this.getActionData(this.component.srcSheet);
    }
  }

  private getSupportedStrategies(): any[] {
    return this.componentService.getComponentStrategies(this.component.component_type);
  }

  changeStrategy(strategyName: string): void {
    if (strategyName) {
      this.strategyInputs = this.strategyService.getStrategyInputs(
        this.component.strategy, this.actionData, this.component.extraOptions.strategySettings
      );
      this.reRenderComponent();
    }
  }

  initKpiStrategyInputs(kpi: any): void {
    if (!kpi.strategy) return;
    if (!kpi.extraOptions) {
      kpi.extraOptions = { strategySettings: {}, componentSettings: {} };
    }
    if (!kpi.extraOptions.strategySettings) {
      kpi.extraOptions.strategySettings = {};
    }
    if (!kpi.extraOptions.componentSettings) {
      kpi.extraOptions.componentSettings = {};
    }
    kpi.kpiStrategyInputs = this.strategyService.getStrategyInputs(
      kpi.strategy, this.actionData, kpi.extraOptions.strategySettings
    );
  }

  changeComponentType(): void {
    // Mirrors the legacy `vm.changeComponentType` exactly — only `componentSettings`
    // is reset, NOT `strategySettings`. Resetting strategySettings here would
    // wipe out a saved component's selections on first render. The user-driven
    // strategy `(ngModelChange)` already clears strategySettings inline.
    this.component.extraOptions.componentSettings = {};
    this.ensureChartSettingsDefaults();
    this.component.supportedStrategies = this.getSupportedStrategies();
    this.component.strategy = null;
    this.reRenderComponent();
  }

  /** True for component types that bind to `extraOptions.componentSettings.chart.*`
   *  in the template (chart-component-tpl.html in the legacy app). */
  hasChartSettings(): boolean {
    return this.component?.component_type === 'chart'
        || this.component?.component_type === 'combinationChart';
  }

  /** Mirrors the legacy `$scope.initChart` — populate default Highcharts
   *  colors when the user picks the waterfall type. */
  initChart(): void {
    const settings = this.component?.extraOptions?.componentSettings;
    if (!settings) return;

    if (settings.chart?.type === 'waterfall') {
      _.set(
        settings,
        'plotOptions.series.color',
        _.get(settings, 'plotOptions.series.color', Highcharts.getOptions().colors?.[1]),
      );
      _.set(
        settings,
        'plotOptions.series.upColor',
        _.get(settings, 'plotOptions.series.upColor', Highcharts.getOptions().colors?.[0]),
      );
    }
  }

  /** Mirrors the legacy `setHighchartFormatting` + `formatChanged` — when the
   *  user picks a non-`None` formatter we drop the inline Highcharts `format`
   *  string (the formatter function takes over) and stash the previous value
   *  so toggling back restores it. */
  formatChanged(key: string, defaultValue: string | null): void {
    const settings = this.component?.extraOptions?.componentSettings;
    if (!settings) return;

    const labelObj = _.get(settings, key);
    if (labelObj) {
      this.safeFormatCopy[key] = labelObj.format;
      if (labelObj.formatter && labelObj.formatter !== 'None') {
        labelObj.format = undefined;
      } else {
        labelObj.format = this.safeFormatCopy[key] || defaultValue || '';
      }
    }
    this.reRenderComponent();
  }

  defaultFilterByChanged(): void {
    if (!this.component.previewSheetData?.data) return;
    this.defaultFilterValues = _.compact(_.uniq(_.map(this.component.previewSheetData.data, (item: any) => item[this.component.extraOptions.strategySettings.default_filter_by])));
  }

  addKpiRow(): void {
    if (!this.component.extraOptions.kpis) {
      this.component.extraOptions.kpis = [];
    }
    const newKpi = _.cloneDeep(this.emptyKpiObj);
    newKpi.srcSheet = this.component.srcSheet;
    newKpi.dashboard_id = this.component.dashboard_id;
    this.component.extraOptions.kpis.push(newKpi);
  }

  removeKpiRow(index: number): void {
    this.component.extraOptions.kpis.splice(index, 1);
  }

  reRenderComponent(): void {
    if (!this.sheetDataSafeCopy || this.sheetDataSafeCopy.length === 0) return;
    this.component.previewSheetData = {
      safeCopyArr: this.fullData ? this.sheetDataSafeCopy : this.sheetDataSafeCopy.slice(0, 50),
      data: this.fullData ? this.sheetDataSafeCopy : this.sheetDataSafeCopy.slice(0, 50),
      isLoaded: true
    };
    console.log('[EditComponent] reRenderComponent →', {
      strategy: this.component.strategy,
      strategySettings: _.cloneDeep(this.component.extraOptions?.strategySettings),
      previewRows: this.component.previewSheetData.data?.length || 0,
    });
    // Resolver expects an envelope `{ newComponentObj, doUpdate }` (mirrors the
    // legacy AngularJS event payload). When previewMode is on, the resolver
    // re-renders against `previewSheetData` regardless of `doUpdate`.
    this.eventService.broadcast(
      'component-' + this.component.component_id + ':reload',
      { newComponentObj: this.component, doUpdate: false },
    );
  }

  getActionData(sheetName?: string): void {
    this.actionLoading = true;
    const sheet: any = this.sheetData.find(item =>
      (item.name === sheetName) || (item.name === this.component.srcSheet)
    );
    console.log('[EditComponent] getActionData →', { sheetName, srcSheet: this.component.srcSheet, foundSheet: sheet });
    if (!sheet) {
      console.warn('[EditComponent] getActionData: no sheet found in sheetData. sheetData =', this.sheetData);
      this.actionLoading = false;
      return;
    }

    if (sheet.data && sheet.data.length > 0) {
      console.log('[EditComponent] getActionData: using cached sheet.data, rows =', sheet.data.length);
      this.resolveData(sheet.data);
      return;
    }

    // Match the legacy URL pattern from `addChartComponentCtrl.getActionData`:
    //   POST `${base_url}/${sheet.action_id}` for persisted sheets,
    //   POST `${base_url}/loadJsonObject?action_code=${action_code|action_id}`
    //   when the sheet has not been saved yet (no sheet_key).
    //
    // Legacy also merges `GlobalService.globalParams` (tax_year, scenario,
    // jcd_key, etc.) into the body — without those, server-side actions like
    // `getTransactionCount` short-circuit and return only a 1-row stub, so
    // the chart silently renders against ~2 columns and looks empty.
    const baseUrl = environment.base_url;
    const actionCode = sheet.action_code || sheet.action_id;
    const url = sheet.sheet_key
      ? `${baseUrl}/${sheet.action_id}`
      : `${baseUrl}/loadJsonObject?action_code=${actionCode}`;
    const globalParams = this.globalService?.global?.globalParams || {};
    const body = { ...globalParams, is_admin: 'Y' };
    console.log('[EditComponent] getActionData: POSTing →', url, 'body:', body);

    this.http.post<any>(url, body, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        const sheetData = Array.isArray(response) ? response : (response?.jsonObject || []);
        console.log('[EditComponent] getActionData response →', {
          rawShape: Array.isArray(response) ? 'array' : typeof response,
          rows: sheetData.length,
          sampleRow: sheetData[0],
        });
        this.resolveData(sheetData);
      },
      error: (err) => {
        console.error('[EditComponent] getActionData error:', err);
        this.actionLoading = false;
      }
    });
  }

  private resolveData(data: any[]): void {
    this.sheetDataSafeCopy = data;
    this.actionData = data.length > 0 ? Object.keys(data[0]) : [];
    this.actionDataSafeCopy = [...this.actionData];

    // Diagnostic — make it easy to spot column-name mismatches between the
    // saved `strategySettings` (e.g. `categories_by: 'TAX_YEAR'`) and the
    // columns the API actually returned for the current sheet.
    console.log('[EditComponent] resolveData →', {
      rows: data?.length ?? 0,
      actionData: this.actionData,
      strategy: this.component?.strategy,
      strategySettings: _.cloneDeep(this.component?.extraOptions?.strategySettings),
      kpis: this.component.extraOptions?.kpis?.length || 0,
    });

    if (this.component?.strategy) {
      this.strategyInputs = this.strategyService.getStrategyInputs(
        this.component.strategy, this.actionData, this.component.extraOptions.strategySettings
      );
      console.log('[EditComponent] strategyInputs built:', _.cloneDeep(this.strategyInputs));
    }
    this.component.previewSheetData = {
      safeCopyArr: this.fullData ? data : data.slice(0, 50),
      data: this.fullData ? data : data.slice(0, 50),
      isLoaded: true
    };

    this.emptyKpiObj.previewMode = true;
    this.emptyKpiObj.previewSheetData = this.component.previewSheetData;

    if (this.component.extraOptions?.kpis) {
      this.component.extraOptions.kpis.forEach((kpi: any) => {
        kpi.previewMode = true;
        kpi.previewSheetData = this.component.previewSheetData;
        this.initKpiStrategyInputs(kpi);
      });
    }

    setTimeout(() => {
      this.actionLoading = false;
      this.chartView = true;
    }, 500);

    this.eventService.broadcast('component-object:loaded', this.component);
  }

  /**
   * Opens a read-only modal with the source code of the selected strategy
   * function. Mirrors the legacy `vm.showFunction` (`adminDashboardCtrl.js`),
   * which used `view-strategy-function-tpl.html`.
   */
  showFunction(strategy: string): void {
    if (!strategy) return;
    const funcStr = this.strategyService.getStrategyFunctionString(strategy);
    if (!funcStr) return;

    const config: MatDialogConfig = {
      width: '70vw',
      maxWidth: '900px',
      panelClass: 'strategy-function-dialog-panel',
      backdropClass: 'mat-dialog-backdrop',
      disableClose: false,
      data: { strategyName: strategy, func: funcStr },
    };
    this.dialog.open(StrategyFunctionDialogComponent, config);
  }

  /** Used by the modal-footer Save button. Returns `false` while
   *  `@ViewChild('chartForm')` is still resolving (otherwise we'd flip
   *  `disabled` from `true` to `false` between change-detection passes and
   *  trigger `ExpressionChangedAfterItHasBeenCheckedError`). The Save
   *  handler itself re-validates before doing anything destructive. */
  isSaveDisabled(): boolean {
    return !!this.chartForm && !!this.chartForm.invalid;
  }

  save(): void {
    // Guard against early clicks before the form is initialised, plus
    // re-validate once it is.
    if (!this.chartForm || this.chartForm.invalid) return;

    const sheet = this.sheetData.find(item => item.name === this.component.srcSheet);
    const extraOptions = _.cloneDeep(this.component.extraOptions);

    if (extraOptions.kpis) {
      extraOptions.kpis = extraOptions.kpis.map((kpi: any) => {
        kpi.component_id = this.component.component_id;
        if (!kpi.dashboard_id) kpi.dashboard_id = this.data.dashboard_id;
        return _.omit(kpi, ['previewMode', 'previewSheetData', 'data', 'kpiStrategyInputs']);
      });
    }

    const componentPayload = {
      component_key: this.component.component_id,
      comp_name: this.component.comp_name,
      comp_title: this.component.title,
      comp_columns: this.component.columns,
      comp_strategy: this.component.strategy,
      height: this.component.height,
      filterable: this.component.filterable ? 'Y' : 'N',
      comp_src_sheet_index: this.component.srcSheet,
      sheet_key: (sheet as any)?.sheet_key || '',
      comp_type: this.component.component_type,
      dashboard_id: this.data.dashboard_id,
      comp_extra_options: JSON.stringify(extraOptions),
      rowindex: this.data.rowIndex,
      row_title: this.component.row_title
    };

    const url = `${environment.base_url}/createComponent`;
    const params = { comp_data: JSON.stringify(componentPayload) };

    this.http.post<any>(url, params, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        if (response?.callSuccess === '1') {
          this.dialogRef.close(this.component);
        } else {
          console.error('[EditComponent] Save failed:', response);
        }
      },
      error: (err) => {
        console.error('[EditComponent] Save error:', err);
      }
    });
  }

  applyJsonObj(): void {
    try {
      const parsedObj = safeJsonInput<any>(this.compJsonObj, null, 'edit-component-dialog.parse1');
      parsedObj.title = this.component.title;
      parsedObj.dashboard_id = this.component.dashboard_id;
      parsedObj.component_id = this.component.component_id;
      parsedObj.rowIndex = this.component.rowIndex;
      this.component = parsedObj;
      this.ensureChartSettingsDefaults();
      this.jsonView = false;
      this.getActionData(this.component.srcSheet);
    } catch (e) {
      console.error('[EditComponent] Invalid JSON:', e);
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  /** Make sure `extraOptions.componentSettings` has the keys the chart
   *  settings template binds to so we never read through `undefined`. */
  private ensureChartSettingsDefaults(): void {
    if (!this.component) return;
    if (!this.component.extraOptions) {
      this.component.extraOptions = { componentSettings: {}, strategySettings: {} };
    }
    if (!this.component.extraOptions.componentSettings) {
      this.component.extraOptions.componentSettings = {};
    }
    this.component.extraOptions.componentSettings = _.merge(
      _.cloneDeep(CHART_SETTINGS_DEFAULTS),
      this.component.extraOptions.componentSettings,
    );
  }
}
